/**
 * Expense READ endpoints — request schemas + wire types.
 *
 * Unlike the write schemas in `expenses.ts` (Pattern B/C — RPCs behind the
 * backend), these serve plain RLS-scoped reads: the Hono handlers run the
 * queries through the per-request user client, so org RLS does the
 * authorization (see docs/BACKEND_API.md → "Read endpoints"; template:
 * clearingReads.ts).
 *
 * The wire interfaces below are shared by the API handlers (response
 * construction) and the web hooks (unwrap<T> targets) so the two sides
 * cannot drift. `apps/web/src/hooks/useExpenses.ts` re-derives its public
 * `Expense`/`ExpenseLineItem` types from these (narrowing `tax_treatment`
 * to the web TaxTreatment union) so hook consumers see the same shapes as
 * before the Hono migration.
 */
import { z } from "zod";
import { makeDateWindowSchema } from "./dateWindow.js";
/**
 * Every column of `ledger_entries`, pinned explicitly. The legacy browser
 * select used `entries:ledger_entries(*)`; the handler interpolates this
 * list instead so the wire type below and the actual payload cannot drift —
 * a future ledger_entries column must be consciously added to BOTH.
 */
export const LEDGER_ENTRY_COLUMNS = "id, transaction_id, organization_id, account_id, debit_amount, credit_amount, description, tax_rate_id, fund_id, vat_settlement_id, is_reverse_charge, custom_fields, created_at";
// ---------------------------------------------------------------------------
// Query-param schemas
// ---------------------------------------------------------------------------
/** Query params for GET /orgs/:orgId/expenses/index. */
export const expenseIndexQuerySchema = z.object({
    projectId: z.string().uuid().optional(),
    businessId: z.string().uuid().optional(),
    personId: z.string().uuid().optional(),
});
/** Query params for GET /orgs/:orgId/expenses/picker. */
export const expensePickerQuerySchema = z.object({
    search: z.string().max(200).optional(),
    limit: z.coerce.number().int().positive().max(200).optional(),
});
/** Comma-separated uuid list (capped) — labels lookups. */
const uuidListSchema = z
    .string()
    .optional()
    .refine((v) => {
    if (v === undefined || v === "")
        return true;
    const parts = v.split(",");
    return (parts.length <= 500 &&
        parts.every((p) => z.string().uuid().safeParse(p).success));
}, { message: "Expected a comma-separated list of at most 500 uuids" });
/** Query params for GET /orgs/:orgId/expenses/labels. */
export const expenseLabelsQuerySchema = z.object({
    expenseIds: uuidListSchema,
    lineItemIds: uuidListSchema,
});
/** Query params for GET /orgs/:orgId/expenses/:expenseId/line-items. */
export const expenseLineItemsQuerySchema = z.object({
    currentAssetId: z.string().uuid().optional(),
});
/**
 * Query params for GET /orgs/:orgId/expenses/period-totals — an inclusive
 * `{ from, to }` window (real calendar days, ordered, ≤ 36 months — the same
 * cap `get_pnl_monthly_account_totals` enforces) plus an optional project
 * scope. `.and`, not `.extend`: the window factory returns a ZodEffects.
 */
export const expensePeriodTotalsQuerySchema = makeDateWindowSchema(36).and(z.object({ projectId: z.string().uuid().optional() }));
//# sourceMappingURL=expensesReads.js.map